import { useAuthenticatedQuery } from 'flow/hooks/api/useAuthenticatedQuery';
import AiCoworkerResource from 'flow/api/aicoworker';
import { ExtensionKey } from 'flow/types/EntityKeys';

interface AICoworkerStatsResponse {
    totalNumberOfIncomingAnsweredCalls: number;
    totalTimeOfIncomingAnsweredCallSeconds: number;
    averageTimePerCallSeconds: number;
}

export interface AICoworkerStats {
    numberOfCalls: number;
    totalTalkTime: number;
    avgTalkTime: number;
}

type IntervalResolution = 'today' | 'week' | 'month';

const calculateDateRange = (resolution: IntervalResolution) => {
    const fromDate = new Date();

    if (resolution === 'today') {
        fromDate.setUTCHours(0, 0, 0, 0);
    } else if (resolution === 'week') {
        fromDate.setUTCDate(fromDate.getUTCDate() - 7);
    } else if (resolution === 'month') {
        fromDate.setUTCMonth(fromDate.getUTCMonth() - 1);
    }

    return fromDate.toISOString();
};

export const useAICoworkerStats = (
    extensionKey: ExtensionKey,
    resolution: IntervalResolution = 'today',
) => {
    const fromDate = calculateDateRange(resolution);

    const { data, isLoading, error, refetch } =
        useAuthenticatedQuery<AICoworkerStats>({
            queryKey: ['aiCoworkerStats', extensionKey, resolution],
            queryFn: () =>
                new Promise<AICoworkerStats>((resolve, reject) => {
                    AiCoworkerResource.getAICoworkerStats(
                        {
                            onResult: (json: AICoworkerStatsResponse) => {
                                const stats: AICoworkerStats = {
                                    numberOfCalls:
                                        json.totalNumberOfIncomingAnsweredCalls ||
                                        0,
                                    totalTalkTime:
                                        json.totalTimeOfIncomingAnsweredCallSeconds ||
                                        0,
                                    avgTalkTime:
                                        json.averageTimePerCallSeconds || 0,
                                };
                                resolve(stats);
                            },
                            onError: reject,
                            onClientError: reject,
                        },
                        extensionKey,
                        fromDate,
                    );
                }),
            refetchInterval: 30000, // Refetch every 30 seconds
            staleTime: 30000, // Consider data stale after 30 seconds
        });

    return {
        data,
        isLoading,
        error,
        refetch,
    };
};
